// Records — the full database browser: every scraped profile, server-side
// search/filter/sort/pagination, CSV export, full-detail drawer, bulk actions.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, Download, X, ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
  Database, Trash2, Ban, Phone, Globe, MapPin, BadgeCheck, Flame, RefreshCw,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import {
  Chip, Btn, inputStyle, useToasts, EmptyState, ConfirmDialog,
  STATUS_TONE, SOURCE_TONE, fmtNum, fmtDate, fmtDateTime, Spinner,
} from './ui';

const EMAIL_STATUSES = ['', 'pending', 'sent', 'failed', 'bounced', 'invalid', 'do_not_contact'];
const SOURCES = ['', 'google', 'osm', 'scraper'];

const selectStyle = { ...inputStyle, paddingRight: 28 };

const RecordsBrowser = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({ contacted: '', email_status: '', source: '', location: '', date_from: '', date_to: '' });
  const [sortBy, setSortBy] = useState('extracted_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selected, setSelected] = useState(new Set());
  const [drawer, setDrawer] = useState(null); // {loading, data}
  const [confirm, setConfirm] = useState(null); // {action}
  const [exporting, setExporting] = useState(false);
  const [push, toasts] = useToasts();
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  const queryParams = useCallback(() => ({
    search,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
  }), [search, sortBy, sortOrder, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getRecords({ ...queryParams(), limit: pageSize, offset: page * pageSize });
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      push(`Failed to load records: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [queryParams, page, pageSize, push]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(0); };

  const toggleSort = (col) => {
    if (sortBy === col) setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(col); setSortOrder('desc'); }
    setPage(0);
  };

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const togglePage = () => setSelected((s) => {
    const n = new Set(s);
    if (allOnPageSelected) rows.forEach((r) => n.delete(r.id));
    else rows.forEach((r) => n.add(r.id));
    return n;
  });

  const openDrawer = async (row) => {
    setDrawer({ loading: true, row });
    try {
      const data = await apiService.getRecordFull(row.id);
      setDrawer({ loading: false, row, data });
    } catch (e) {
      setDrawer({ loading: false, row, error: e.message });
    }
  };

  const runBulk = async () => {
    const action = confirm.action;
    setConfirm(null);
    try {
      const res = await apiService.bulkRecords([...selected], action);
      if (res.error) throw new Error(res.error);
      push(`${res.affected} record${res.affected === 1 ? '' : 's'} ${action === 'delete' ? 'deleted' : 'marked do-not-contact'}`);
      setSelected(new Set());
      load();
    } catch (e) {
      push(`Bulk action failed: ${e.message}`, 'error');
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      await apiService.exportRecordsCsv(queryParams());
      push('CSV export downloaded');
    } catch (e) {
      push(`Export failed: ${e.message}`, 'error');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const SortHead = ({ col, children, style = {} }) => (
    <th
      onClick={() => toggleSort(col)}
      style={{
        padding: '11px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700,
        color: sortBy === col ? 'var(--c-primary)' : 'var(--c-text-3)',
        textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer',
        whiteSpace: 'nowrap', userSelect: 'none', ...style,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {children}
        {sortBy === col && (sortOrder === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />)}
      </span>
    </th>
  );

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 20px 90px' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, color: 'var(--c-text)' }}>
            Records
          </div>
          <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginTop: 3 }}>
            {loading ? 'Loading…' : `${fmtNum(total)} record${total === 1 ? '' : 's'} in your database`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn onClick={load} title="Refresh"><RefreshCw size={14} /> Refresh</Btn>
          <Btn tone="primary" onClick={doExport} disabled={exporting}>
            <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
          </Btn>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
        background: 'var(--c-bg-1)', border: '1px solid var(--c-border)',
        borderRadius: 14, padding: 12, marginBottom: 16,
      }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
          <Search size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }} />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email, company, location…"
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', paddingLeft: 32 }}
          />
        </div>
        <select value={filters.contacted} onChange={(e) => setFilter('contacted', e.target.value)} style={selectStyle}>
          <option value="">Contacted: all</option>
          <option value="1">Contacted</option>
          <option value="0">Not contacted</option>
        </select>
        <select value={filters.email_status} onChange={(e) => setFilter('email_status', e.target.value)} style={selectStyle}>
          {EMAIL_STATUSES.map((s) => <option key={s} value={s}>{s ? `Status: ${s.replace(/_/g, ' ')}` : 'Status: all'}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setFilter('source', e.target.value)} style={selectStyle}>
          {SOURCES.map((s) => <option key={s} value={s}>{s ? `Source: ${s}` : 'Source: all'}</option>)}
        </select>
        <input
          value={filters.location}
          onChange={(e) => setFilter('location', e.target.value)}
          placeholder="Location…"
          style={{ ...inputStyle, width: 120 }}
        />
        <input type="date" value={filters.date_from} onChange={(e) => setFilter('date_from', e.target.value)} style={inputStyle} title="Scraped from" />
        <input type="date" value={filters.date_to} onChange={(e) => setFilter('date_to', e.target.value)} style={inputStyle} title="Scraped up to" />
        {(search || Object.values(filters).some(Boolean)) && (
          <Btn small onClick={() => { setSearchInput(''); setFilters({ contacted: '', email_status: '', source: '', location: '', date_from: '', date_to: '' }); setPage(0); }}>
            <X size={13} /> Clear
          </Btn>
        )}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--c-bg-1)', border: '1px solid var(--c-border)', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--c-border)', background: 'var(--c-surface)' }}>
                <th style={{ padding: '11px 12px', width: 34 }}>
                  <input type="checkbox" checked={allOnPageSelected} onChange={togglePage} style={{ cursor: 'pointer' }} />
                </th>
                <SortHead col="name">Business</SortHead>
                <SortHead col="email">Email</SortHead>
                <th style={{ padding: '11px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone</th>
                <SortHead col="location">Location</SortHead>
                <SortHead col="source">Source</SortHead>
                <SortHead col="email_sent_at">Contacted</SortHead>
                <SortHead col="extracted_at">Scraped</SortHead>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center' }}><Spinner size={22} /></td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8}>
                  <EmptyState icon={Database} title="No records match" sub="Try clearing a filter or changing the search." />
                </td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => openDrawer(r)}
                  style={{ borderBottom: '1px solid var(--c-border)', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--c-surface-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} style={{ cursor: 'pointer' }} />
                  </td>
                  <td style={{ padding: '10px 12px', maxWidth: 220 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.company || r.name || '—'}
                    </div>
                    {r.title && <div style={{ fontSize: 11.5, color: 'var(--c-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--c-text-2)', maxWidth: 230 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email || '—'}</span>
                      {r.email_verified && <BadgeCheck size={13} style={{ color: 'var(--c-primary)', flexShrink: 0 }} title="Verified" />}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--c-text-2)', whiteSpace: 'nowrap' }}>{r.phone || '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--c-text-2)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.location || '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {r.source ? <Chip tone={SOURCE_TONE[r.source] || 'gray'}>{r.source}</Chip> : <span style={{ color: 'var(--c-text-3)' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {r.email_sent
                      ? <Chip tone={STATUS_TONE[r.email_status] || 'green'}>{(r.email_status || 'sent').replace(/_/g, ' ')}{r.email_sent_at ? ` · ${fmtDate(r.email_sent_at)}` : ''}</Chip>
                      : (r.email_status && r.email_status !== 'pending'
                        ? <Chip tone={STATUS_TONE[r.email_status] || 'gray'}>{r.email_status.replace(/_/g, ' ')}</Chip>
                        : <Chip tone="gray">not yet</Chip>)}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--c-text-3)', whiteSpace: 'nowrap' }}>{fmtDate(r.extracted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 12.5, color: 'var(--c-text-3)' }}>
            Page {page + 1} of {fmtNum(totalPages)} · showing {rows.length} of {fmtNum(total)}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }} style={{ ...selectStyle, padding: '6px 10px', fontSize: 12.5 }}>
              {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <Btn small onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}><ChevronLeft size={14} /> Prev</Btn>
            <Btn small onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next <ChevronRight size={14} /></Btn>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 150,
          display: 'flex', alignItems: 'center', gap: 12,
          background: 'var(--c-bg)', border: '1px solid var(--c-border-bright)',
          borderRadius: 14, padding: '10px 18px', boxShadow: 'var(--shadow-elevated)',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>{selected.size} selected</span>
          <Btn small onClick={() => setConfirm({ action: 'do_not_contact' })}><Ban size={13} /> Do not contact</Btn>
          <Btn small tone="danger" onClick={() => setConfirm({ action: 'delete' })}><Trash2 size={13} /> Delete</Btn>
          <Btn small onClick={() => setSelected(new Set())}><X size={13} /></Btn>
        </div>
      )}

      <ConfirmDialog
        open={!!confirm}
        danger={confirm?.action === 'delete'}
        title={confirm?.action === 'delete' ? `Delete ${selected.size} record${selected.size === 1 ? '' : 's'}?` : `Mark ${selected.size} as do-not-contact?`}
        body={confirm?.action === 'delete'
          ? 'This permanently removes the selected records from your database. This cannot be undone.'
          : 'The selected records will be excluded from all future campaigns and follow-ups. You can still see them under Status: do not contact.'}
        confirmLabel={confirm?.action === 'delete' ? 'Delete' : 'Mark do-not-contact'}
        onConfirm={runBulk}
        onCancel={() => setConfirm(null)}
      />

      {/* Detail drawer */}
      {drawer && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 260 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'var(--c-overlay)' }} onClick={() => setDrawer(null)} />
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 460, maxWidth: '94vw',
            background: 'var(--c-bg)', borderLeft: '1px solid var(--c-border)',
            boxShadow: 'var(--shadow-elevated)', overflowY: 'auto', padding: 24,
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 19, fontWeight: 700, color: 'var(--c-text)' }}>
                  {drawer.row.company || drawer.row.name}
                </div>
                {drawer.row.title && <div style={{ fontSize: 13, color: 'var(--c-text-2)', marginTop: 3 }}>{drawer.row.title}</div>}
              </div>
              <button onClick={() => setDrawer(null)} style={{ background: 'none', border: 'none', color: 'var(--c-text-3)', cursor: 'pointer', padding: 4 }}>
                <X size={19} />
              </button>
            </div>

            {drawer.loading && <div style={{ padding: 40, textAlign: 'center' }}><Spinner size={22} /></div>}
            {drawer.error && <div style={{ marginTop: 20, color: 'var(--c-red)', fontSize: 13 }}>{drawer.error}</div>}

            {drawer.data && (() => {
              const p = drawer.data.profile || {};
              const c = drawer.data.campaign || {};
              const lead = drawer.data.lead;
              const replies = drawer.data.replies || [];
              const demo = drawer.data.demo;
              const Section = ({ title, children }) => (
                <div style={{ marginTop: 22 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--c-text-3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 9 }}>{title}</div>
                  {children}
                </div>
              );
              const Row = ({ icon: Icon, label, children }) => (
                <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '5px 0', fontSize: 13, color: 'var(--c-text-2)' }}>
                  {Icon && <Icon size={14} style={{ marginTop: 2, color: 'var(--c-text-3)', flexShrink: 0 }} />}
                  {label && <span style={{ color: 'var(--c-text-3)', minWidth: 84 }}>{label}</span>}
                  <span style={{ color: 'var(--c-text)', wordBreak: 'break-word', minWidth: 0 }}>{children}</span>
                </div>
              );
              return (
                <>
                  <Section title="Contact">
                    <Row label="Email">{p.email || '—'} {p.email_verified ? <Chip tone="green" style={{ marginLeft: 6 }}>verified</Chip> : null}</Row>
                    {p.phone && <Row icon={Phone} label="Phone">{p.phone}</Row>}
                    {p.location && <Row icon={MapPin} label="Location">{p.location}</Row>}
                    {p.profile_url && (
                      <Row icon={Globe} label="Website">
                        <a href={p.profile_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)' }}>{p.profile_url}</a>
                      </Row>
                    )}
                    {p.source && <Row label="Source"><Chip tone={SOURCE_TONE[p.source] || 'gray'}>{p.source}</Chip></Row>}
                    {p.search_terms && <Row label="Found via">{p.search_terms}</Row>}
                    <Row label="Scraped">{fmtDateTime(p.extracted_at)}</Row>
                  </Section>

                  {(p.specialization || p.about) && (
                    <Section title="About">
                      {p.specialization && <div style={{ fontSize: 13, color: 'var(--c-text)', marginBottom: 6 }}>{p.specialization}</div>}
                      {p.about && <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', whiteSpace: 'pre-wrap', lineHeight: 1.5, maxHeight: 140, overflowY: 'auto' }}>{p.about}</div>}
                    </Section>
                  )}

                  {p.icebreaker && (
                    <Section title="AI icebreaker">
                      <div style={{ fontSize: 13, color: 'var(--c-text)', background: 'var(--c-primary-glow)', border: '1px solid var(--c-border-bright)', borderRadius: 10, padding: 12, lineHeight: 1.5 }}>
                        {p.icebreaker}
                      </div>
                    </Section>
                  )}

                  <Section title="Outreach">
                    <Row label="Status">
                      <Chip tone={c.email_sent ? (STATUS_TONE[c.email_status] || 'green') : 'gray'}>
                        {c.email_sent ? (c.email_status || 'sent').replace(/_/g, ' ') : (c.email_status === 'do_not_contact' ? 'do not contact' : 'not contacted')}
                      </Chip>
                    </Row>
                    {c.email_sent_at && <Row label="Sent">{fmtDateTime(c.email_sent_at)}</Row>}
                    {c.sent_via && <Row label="From">{c.sent_via}</Row>}
                    {c.email_subject && <Row label="Subject">{c.email_subject}</Row>}
                    {Number(c.follow_up_count) > 0 && <Row label="Follow-ups">{c.follow_up_count} (last {fmtDate(c.last_follow_up_at)})</Row>}
                  </Section>

                  {lead && (
                    <Section title="Pipeline">
                      <Row label="Stage"><Chip tone={lead.stage === 'won' ? 'green' : lead.stage === 'lost' ? 'red' : 'blue'}>{lead.stage}</Chip></Row>
                      {lead.score != null && <Row icon={Flame} label="Score">{lead.score}/100</Row>}
                      {lead.deal_value != null && Number(lead.deal_value) > 0 && <Row label="Value">R {fmtNum(lead.deal_value)}</Row>}
                    </Section>
                  )}

                  {demo && (
                    <Section title="Demo">
                      <Row label="Status"><Chip tone={demo.open_count > 0 ? 'green' : 'yellow'}>{demo.status}{demo.open_count > 0 ? ` · ${demo.open_count} opens` : ''}</Chip></Row>
                      {demo.sent_at && <Row label="Sent">{fmtDateTime(demo.sent_at)} via {demo.sent_via}</Row>}
                      {demo.demo_url && (
                        <Row label="Link"><a href={demo.demo_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)' }}>{demo.demo_url}</a></Row>
                      )}
                    </Section>
                  )}

                  {replies.length > 0 && (
                    <Section title={`Conversation (${replies.length})`}>
                      {replies.map((r, i) => (
                        <div key={i} style={{
                          padding: 11, borderRadius: 10, marginBottom: 8,
                          background: r.direction === 'inbound' ? 'var(--c-surface)' : 'var(--c-primary-glow)',
                          border: '1px solid var(--c-border)',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, color: 'var(--c-text-3)', marginBottom: 4 }}>
                            <span style={{ fontWeight: 600 }}>{r.direction === 'inbound' ? 'They wrote' : 'We wrote'}{r.intent ? ` · ${r.intent.replace(/_/g, ' ')}` : ''}</span>
                            <span>{fmtDateTime(r.received_at)}</span>
                          </div>
                          <div style={{ fontSize: 12.5, color: 'var(--c-text-2)', lineHeight: 1.45 }}>{r.snippet}</div>
                        </div>
                      ))}
                    </Section>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {toasts}
    </div>
  );
};

export default RecordsBrowser;
